import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Shield } from 'lucide-react';
import { getSettings } from '../utils/settingsCache';

const FALLBACK_SECTIONS = [
  { id: 1, title: "Overview", highlight: false, content: "This website (www.solvibetoken.com) is a non-commercial, purely informational community website about the SOLVIBE ($SVB) token project.\n\nIMPORTANT: This website does NOT collect, store, or process any personal user data. There are no user accounts, no registration forms, no newsletter subscriptions, no analytics tracking, and no marketing cookies." },
  { id: 2, title: "Data Controller", highlight: false, content: "This website is operated by community contributors on a voluntary, non-commercial basis.\n\nContact: support@solvibetoken.com\nAddress: Ferizaj, Kosovo 70000" },
  { id: 3, title: "Website Hosting", highlight: false, content: "This website is hosted on Railway (Railway Corp., USA). Railway operates the server infrastructure and may automatically collect and process limited technical information required for secure, stable operation — including IP addresses, access timestamps, browser type, and device details.\n\nThis technical data is processed solely for infrastructure security and stability purposes and is not used for marketing or profiling." },
  { id: 4, title: "What Data We Collect", highlight: false, content: "We do NOT actively collect any personal data.\n\nThe only data that may be processed is standard server log data (automatically generated by hosting infrastructure):\n• IP address (anonymized where possible)\n• Browser type and version\n• Operating system\n• Access time and date\n• Pages accessed\n\nThis data is used exclusively for security monitoring and is never sold, shared, or used for marketing purposes." },
  { id: 5, title: "Cookies", highlight: false, content: "This website uses ONLY technically necessary cookies — strictly limited to:\n• Session management and site functionality\n• Security (CSRF protection)\n\nWe do NOT use:\n• Marketing cookies\n• Analytics cookies (no Google Analytics, no Mixpanel, etc.)\n• Tracking cookies or fingerprinting technologies\n• Third-party advertising cookies" },
  { id: 6, title: "External Links & Third-Party Services", highlight: false, content: "This website contains links to external platforms including decentralized exchanges, analytics tools, and social media platforms. When you click these links, you leave this website and are subject to the respective platform's privacy policy.\n\nThese links are provided for informational purposes only. We do NOT control external platforms and are not responsible for the privacy practices of any third party." },
  { id: 7, title: "Blockchain Data", highlight: true, content: "Transactions on the Solana blockchain are inherently public and immutable. If you interact with the SOLVIBE ($SVB) token or any other blockchain asset, your wallet address and transaction details are permanently recorded on the public blockchain ledger and are outside the control of this website or its contributors." },
  { id: 8, title: "Your Rights (GDPR & Local Law)", highlight: false, content: "If you are an EU/EEA resident, you have the following rights under the GDPR:\n• Right of access to your personal data\n• Right to rectification of inaccurate data\n• Right to erasure ('right to be forgotten')\n• Right to restriction of processing\n• Right to data portability\n• Right to object to processing\n\nSince we do not collect personal data, most requests will be answered with confirmation that no data is held. Contact us at support@solvibetoken.com for any requests. We will respond within 30 days." },
  { id: 9, title: "Liability Disclaimer", highlight: true, content: "TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE SITE OPERATOR AND COMMUNITY CONTRIBUTORS DISCLAIM ALL LIABILITY FOR ANY DAMAGES ARISING FROM THE USE OF THIS WEBSITE, RELIANCE ON ITS CONTENT, OR ACTIONS TAKEN BASED ON EXTERNAL LINKS.\n\nUse of this website is entirely at your own risk." },
  { id: 10, title: "Jurisdiction", highlight: false, content: "This Privacy Policy is governed by the laws of Kosovo. Any disputes arising from this Privacy Policy shall be subject to the exclusive jurisdiction of the competent courts of Kosovo." },
  { id: 11, title: "Notice", highlight: false, content: "www.solvibetoken.com is an independent, non-commercial community information website. Nothing on this site constitutes financial advice, an investment offer, or a solicitation to buy or sell any digital asset.\n\nContact: support@solvibetoken.com" },
];

const PrivacyPolicyModal = ({ isOpen, onClose }) => {
  const [sections, setSections] = useState(FALLBACK_SECTIONS);
  const [lastUpdated, setLastUpdated] = useState('June 2026');

  useEffect(() => {
    if (!isOpen) return;
    getSettings().then(data => {
      if (data?.privacy_sections) {
        try {
          const parsed = JSON.parse(data.privacy_sections);
          if (Array.isArray(parsed) && parsed.length > 0) setSections(parsed);
        } catch (_) {}
      }
      if (data?.legal_last_updated) setLastUpdated(data.legal_last_updated);
    });
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[2147483647] bg-black/80 backdrop-blur-md flex items-center justify-center p-4"
        onClick={onClose}
        style={{ zIndex: 2147483647 }}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          className="bg-gray-900 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden border border-gray-700 shadow-2xl"
          onClick={(e) => e.stopPropagation()}
          style={{ zIndex: 2147483647 }}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-solana-primary/10 to-neon-cyan/10 border-b border-gray-700 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-gradient-to-br from-solana-primary to-neon-cyan rounded-full w-10 h-10 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white">Privacy Policy / Legal Notice</h2>
              </div>
              <button
                onClick={onClose}
                aria-label="Close privacy policy"
                className="text-gray-400 hover:text-white transition-colors p-2 rounded-full hover:bg-gray-800"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[70vh] text-gray-300 leading-relaxed">
            <div className="space-y-6">
              {sections.map((section, idx) => (
                <div key={section.id ?? idx}>
                  <h3 className="text-xl font-semibold text-white mb-3 flex items-center gap-2">
                    <span className="w-2 h-2 bg-neon-cyan rounded-full flex-shrink-0"></span>
                    {idx + 1}. {section.title}
                  </h3>
                  {section.highlight ? (
                    <div className="bg-gradient-to-r from-red-500/10 to-orange-500/10 border border-red-500/20 rounded-lg p-4">
                      <p className="text-base text-red-100 whitespace-pre-line font-medium">
                        {section.content}
                      </p>
                    </div>
                  ) : (
                    <p className="text-base whitespace-pre-line">
                      {section.content}
                    </p>
                  )}
                </div>
              ))}

              <div className="text-center pt-4 border-t border-gray-700">
                <p className="text-sm text-gray-500">
                  <strong>Last updated:</strong> {lastUpdated}
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-800/50 border-t border-gray-700 p-4 text-center">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gradient-to-r from-solana-primary to-neon-cyan text-white font-semibold rounded-lg hover:scale-105 transition-all duration-300"
            >
              Close
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PrivacyPolicyModal;
